
import * as React from "react";
import {getGenericEventPropertiesObject} from "sh-instrumentation";
import {InstrumentationService} from "sh-services";
import {instrumentationProperties} from "sh-services/lib/InstrumentationService/lib/instrumentationProperties";
import { getCDLClient } from "sh-services/lib/cdl/CDLClient";
import { TeamMemberRole } from "sh-services/lib/cdl/graphql";
import { TeamInfoStore, TeamStore } from "sh-stores";
import ECSConfigKey from "sh-services/lib/ecsconfigservice/ECSConfigKey";
import ECSConfigService from "sh-services/lib/ecsconfigservice/ECSConfigService";
import { trace } from "owa-trace";

import { THREAD_MEMBERS_BY_ROLE } from "../graphql/queries/threadMembersByRole";
import { IDataLayerClient } from "node_modules/@msteams/core-cdl-sdk/dist/es";

export interface ThreadMember {
    user: {
        objectId: string;
        email: string;
        displayName: string;
        avatarUrl: string;
        type: string;
    };
}

export interface ThreadMembersResult {
    members: ThreadMember[];
    nextLink?: string | null;
    totalCount: number;
}

export interface UseThreadMembersByRoleResult {
    loading: boolean;
    error: Error | null;
    data: ThreadMembersResult | null;
}

export const useThreadMembersByRolePaginated = (enabled: boolean, roles: TeamMemberRole[], threadIdOverride?: string): UseThreadMembersByRoleResult => {
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<Error | null>(null);
    const [data, setData] = React.useState<ThreadMembersResult | null>(null);

    const { teamId } = TeamStore();
    const { teams } = TeamInfoStore();
    // Allow callers to target an arbitrary thread (e.g. counting members for each team in a
    // picker list); fall back to the current team's chat thread when no override is provided.
    const teamChatId = threadIdOverride ?? teams.get(teamId)?.teamChatId;
    const effectivePageSize = ECSConfigService.getECSFeatureSetting(ECSConfigKey.CDLMemberPageSize) ?? 1000;

    React.useEffect(() => {
        let isMounted = true;

        // Helper function to fetch a page
        const fetchPage = async (CDLClient: IDataLayerClient, nextLink?: string | null): Promise<any> => {
            const variables: any = {
                threadId: teamChatId,
                roles: roles,
                pageSize: effectivePageSize
            };

            if (nextLink) {
                variables.nextLink = nextLink;
            }

            const queryResults = CDLClient.executeWithQueryText("query", THREAD_MEMBERS_BY_ROLE, variables, "threadMembersByRole");

            return new Promise((resolve, reject) => {
                queryResults.subscribe(
                    (result: any) => resolve(result),
                    (err: Error) => reject(err)
                );
            });
        };

        const fetchData = async () => {
            if (!teamChatId) {
                if (isMounted) {
                    setLoading(false);
                    setError(new Error("Team chat ID not found"));
                }
                return;
            }

            try {
                const CDLClient = await getCDLClient();
                let allMembers: ThreadMember[] = [];
                let currentNextLink: string | null | undefined = null;
                let totalCount = 0;

                // Fetch first page
                const firstResult = await fetchPage(CDLClient);
                if (!isMounted) {
                    return;
                }

                const firstResponse = firstResult.data.threadMembersByRole;
                trace.info(`[CDL Paginated] First page members received from CDL: ${JSON.stringify(firstResponse)}`);
                allMembers = firstResponse.members || [];
                totalCount = firstResponse.totalCount;
                currentNextLink = firstResponse.nextLink;

                // Fetch additional pages if nextLink exists
                while (currentNextLink && isMounted) {
                    const nextResult = await fetchPage(CDLClient, currentNextLink);
                    if (!isMounted) {
                        return;
                    }

                    const nextResponse = nextResult.data.threadMembersByRole;
                    trace.info(`[CDL Paginated] Additional page members received from CDL: ${JSON.stringify(nextResponse)}`);
                    allMembers = [...allMembers, ...(nextResponse.members || [])];
                    currentNextLink = nextResponse.nextLink;
                }

                if (isMounted) {
                    // Deduplicate members by objectId — CDL may return the same user multiple times
                    // (e.g. once per role when querying multiple roles, or across pagination pages).
                    const uniqueMembersMap = new Map<string, ThreadMember>();
                    allMembers.forEach(member => {
                        if (!uniqueMembersMap.has(member.user.objectId)) {
                            uniqueMembersMap.set(member.user.objectId, member);
                        }
                    });
                    const uniqueMembers = Array.from(uniqueMembersMap.values());

                    trace.info(`[CDL Paginated] All members aggregated from CDL: ${JSON.stringify({
                        totalMembers: uniqueMembers.length,
                        totalCount: totalCount,
                        members: uniqueMembers
                    })}`);
                    InstrumentationService.logEvent(InstrumentationService.events.CDLQuerySuccess, [
                        getGenericEventPropertiesObject(instrumentationProperties.NumOfItems, totalCount),
                        getGenericEventPropertiesObject(
                            instrumentationProperties.TeamSettingsValue,
                            `Fetched ${uniqueMembers.length} members across pages`
                        )
                    ]);

                    setData({
                        members: uniqueMembers,
                        nextLink: null,
                        totalCount: totalCount
                    });
                    setLoading(false);
                }
            } catch (error) {
                if (isMounted) {
                    InstrumentationService.logEvent(InstrumentationService.events.CDLQueryFailed, [
                        getGenericEventPropertiesObject(instrumentationProperties.Error, error.message)
                    ]);
                    setError(error instanceof Error ? error : new Error("Unknown error"));
                    setLoading(false);
                }
            }
        };
        if (enabled) {
            fetchData();
        }

        return () => {
            isMounted = false;
        };
    }, [teamChatId, effectivePageSize]);

    return { loading, error, data };
};
